import classNames from 'classnames';
import { useRef } from 'react';

import { useCourseContext } from 'my-core/CourseContext';

import NestedEditor from '../../NestedEditor';
import CONFIG from './calloutConfig';
import CalloutIcon from './CalloutIcon';

import { inputBaseClasses } from '@mui/material/InputBase';
import MenuItem from '@mui/material/MenuItem';
import Popper from '@mui/material/Popper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

const rootCss = ({ palette, spacing }) => ({
  '&:focus': {
    outline: 'none',
  },
  '.Cal-wrapper': {
    padding: spacing(1, 2, 2),
    borderRadius: spacing(2),

    overflow: 'hidden',
    breakInside: 'avoid',
  },
  '.Cal-header': {
    display: 'flex',
    alignItems: 'center',
    color: palette.common.white,
    marginBottom: spacing(0.5),
  },
  '.Cal-iconWrapper': {
    width: 20,
    height: 20,
    fontSize: 10,
    marginRight: spacing(1),
  },
  '.Cal-editor': {
    alignSelf: 'center',
    flex: '1 1 0',
  },

  ...palette.mapColors(c => ({
    [`.Cal-bgColor_${c}`]: { backgroundColor: palette[c].background },
    [`.Cal-color_${c}`]: { color: palette[c].dark },
  })),
});

// The variant picker is a Popper (own portal); its Select menu is a second portal that only
// `MenuProps` can reach, so the menu's z-index goes through `sx` and the icon styles sit on the icon.
const selectCss = ({ palette, shadows }) => ({
  zIndex: 1,
  [`.${inputBaseClasses.root}`]: {
    boxShadow: shadows[3],
    backgroundColor: palette.background.paper,
  },
});
const selectMenuSx = ({ zIndex }) => ({ zIndex: zIndex.modal + 2 }); // 1 above editor toolbar
const selectIconCss = ({ spacing }) => ({
  display: 'inline-flex',
  width: 24,
  height: 24,
  fontSize: 12,
  marginRight: spacing(2),
});

export default function Callout(props) {
  const { block, blockProps, contentState } = props;

  const entityKey = block.getEntityAt(0);
  const entity = contentState.getEntity(entityKey);
  const { callout_type: calloutVariant } = entity.getData();
  const isActive = block.getKey() === blockProps.focusedBlockKey;

  const courseSlug = useCourseContext();
  const calloutConfig = CONFIG[calloutVariant];
  const effectiveConfig = calloutConfig.contexts?.[courseSlug] || calloutConfig;
  const { color, title } = effectiveConfig;

  const wrapperRef = useRef();

  function changeCalloutVariant(callout_type) {
    const entityKey = block.getEntityAt(0);
    const updatedContentState = contentState.mergeEntityData(entityKey, { callout_type });
    blockProps.onUpdateContentState(updatedContentState);
  }

  // wrap the nested editor in a non-contentEditable div wrapped in a contentEditable div
  // which stops propagation of the 'select' event.  This is needed to avoid the 'getIn' error:
  //    getUpdatedSelectionState.js?e514:33 Uncaught TypeError: Cannot read property 'getIn' of undefined
  //    ...
  return (
    <div
      contentEditable={!blockProps.readOnly}
      css={rootCss}
      onSelect={e => e.stopPropagation()}
      ref={wrapperRef}
      suppressContentEditableWarning
    >
      <div className={classNames('Cal-wrapper', `Cal-bgColor_${color}`)} contentEditable={false}>
        <div className="Cal-header">
          <CalloutIcon className="Cal-iconWrapper" courseContext={courseSlug} type={calloutVariant} />
          <Typography
            className={`Cal-color_${color}`}
            sx={{
              color: 'inherit',
            }}
            variant="overline"
          >
            {title}
          </Typography>
        </div>
        <NestedEditor {...props} className={classNames('Cal-editor', `Cal-color_${color}`)} />
      </div>
      {!blockProps.readOnly && (
        <Popper
          anchorEl={wrapperRef.current}
          css={selectCss}
          modifiers={[
            {
              name: 'offset',
              options: { offset: [0, 10] }, // leave some space between callout and select field
            },
          ]}
          open={isActive}
        >
          <TextField
            select
            size="small"
            slotProps={{ select: { MenuProps: { sx: selectMenuSx } } }}
            value={calloutVariant}
          >
            {Object.entries(CONFIG).map(([k, { title }]) => (
              <MenuItem key={k} onMouseDown={() => changeCalloutVariant(k)} value={k}>
                <CalloutIcon css={selectIconCss} type={k} />
                {title}
              </MenuItem>
            ))}
          </TextField>
        </Popper>
      )}
    </div>
  );
}
